import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Trophy,
  Medal,
  Flame,
  Star,
  Crown,
  Target,
  Users,
  TrendingUp,
  Calendar,
  Award
} from 'lucide-react';

const RANK_ICONS = {
  1: { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-500/20' },
  2: { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-300/20' },
  3: { icon: Medal, color: 'text-orange-400', bg: 'bg-orange-400/20' }
};

export default function LeaderboardPage() {
  const { user, token } = useAuth();
  const [pointsLeaderboard, setPointsLeaderboard] = useState([]);
  const [streaksLeaderboard, setStreaksLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [pointsRes, streaksRes, weeklyRes, myRankRes, challengesRes, myChallengesRes] = await Promise.all([
        axios.get(`${API}/leaderboard/points`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/leaderboard/streaks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/leaderboard/weekly`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/leaderboard/my-rank`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/leaderboard/challenges`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leaderboard/challenges/my`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      setPointsLeaderboard(pointsRes.data || []);
      setStreaksLeaderboard(streaksRes.data || []);
      setWeeklyLeaderboard(weeklyRes.data || []);
      setMyRank(myRankRes.data);
      setChallenges(challengesRes.data || []);
      setMyChallenges(myChallengesRes.data || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const joinChallenge = async (challengeId) => {
    try {
      await axios.post(
        `${API}/leaderboard/challenges/${challengeId}/join`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Joined challenge!');
      fetchData();
    } catch (error) {
      toast.error('Failed to join challenge');
    }
  };

  const isJoined = (challengeId) => {
    return myChallenges.some(c => c.id === challengeId);
  };

  const LeaderboardRow = ({ entry, type = 'points' }) => {
    const rankStyle = RANK_ICONS[entry.rank] || { icon: null, color: 'text-gray-400', bg: 'bg-secondary' };
    const RankIcon = rankStyle.icon;
    const isMe = entry.user_id === user?.id;

    return (
      <div className={`flex items-center gap-4 p-4 rounded-sm transition-colors ${
        isMe ? 'bg-primary/10 border border-primary' : 'bg-secondary/30 hover:bg-secondary/50'
      }`}>
        {/* Rank */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${rankStyle.bg}`}>
          {RankIcon ? (
            <RankIcon className={`w-5 h-5 ${rankStyle.color}`} />
          ) : (
            <span className="font-mono text-lg text-gray-400">{entry.rank}</span>
          )}
        </div>

        {/* User */}
        <div className="flex items-center gap-3 flex-1">
          <Avatar>
            <AvatarFallback className="bg-primary/20 text-primary">
              {entry.user_name?.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className={`font-medium ${isMe ? 'text-primary' : 'text-white'}`}>
              {entry.user_name}
              {isMe && <span className="text-xs ml-2 text-primary">(You)</span>}
            </div>
            {type === 'points' && (
              <div className="text-xs text-gray-400">
                {entry.badge_count} badges • {entry.streak} day streak
              </div>
            )}
            {type === 'streaks' && (
              <div className="text-xs text-gray-400">
                Longest: {entry.longest_streak} days
              </div>
            )}
            {type === 'weekly' && (
              <div className="text-xs text-gray-400">
                {entry.weekly_completions} completions this week
              </div>
            )}
          </div>
        </div>

        {/* Score */}
        <div className="text-right">
          {type === 'points' && (
            <div className="font-mono text-xl text-white">{entry.score.toLocaleString()}</div>
          )}
          {type === 'streaks' && (
            <div className="flex items-center gap-1">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-mono text-xl text-white">{entry.streak}</span>
            </div>
          )}
          {type === 'weekly' && (
            <div className="font-mono text-xl text-white">{entry.weekly_completions}</div>
          )}
          <div className="text-xs text-gray-400">
            {type === 'points' ? 'points' : type === 'streaks' ? 'days' : 'activities'}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="leaderboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          <span className="text-primary">LEADER</span>BOARD
        </h1>
        <p className="text-gray-400 mt-1">Compete with the community and climb the ranks</p>
      </div>

      {/* My Stats */}
      {myRank && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical text-center">
            <Trophy className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{myRank.my_stats.total_points}</div>
            <div className="text-xs text-gray-400">Total Points</div>
            <div className="text-sm text-primary mt-1">Rank #{myRank.points_rank}</div>
          </div>
          <div className="card-tactical text-center">
            <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{myRank.my_stats.current_streak}</div>
            <div className="text-xs text-gray-400">Current Streak</div>
            <div className="text-sm text-primary mt-1">Rank #{myRank.streak_rank}</div>
          </div>
          <div className="card-tactical text-center">
            <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{myRank.my_stats.badge_count}</div>
            <div className="text-xs text-gray-400">Badges Earned</div>
          </div>
          <div className="card-tactical text-center">
            <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">Top {Math.round(100 - myRank.percentile.points)}%</div>
            <div className="text-xs text-gray-400">Percentile</div>
          </div>
        </div>
      )}

      {/* Leaderboard Tabs */}
      <Tabs defaultValue="points" className="w-full">
        <TabsList className="bg-secondary/50 border border-border">
          <TabsTrigger value="points" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Trophy className="w-4 h-4 mr-2" />
            Points
          </TabsTrigger>
          <TabsTrigger value="streaks" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Flame className="w-4 h-4 mr-2" />
            Streaks
          </TabsTrigger>
          <TabsTrigger value="weekly" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" />
            This Week
          </TabsTrigger>
          <TabsTrigger value="challenges" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Target className="w-4 h-4 mr-2" />
            Challenges
          </TabsTrigger>
        </TabsList>

        <TabsContent value="points" className="mt-6">
          <div className="space-y-3">
            {pointsLeaderboard.length === 0 ? (
              <div className="text-center py-12 card-tactical">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No rankings yet</h3>
                <p className="text-gray-400">Start earning points to appear on the leaderboard!</p>
              </div>
            ) : (
              pointsLeaderboard.map((entry) => (
                <LeaderboardRow key={entry.user_id} entry={entry} type="points" />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="streaks" className="mt-6">
          <div className="space-y-3">
            {streaksLeaderboard.length === 0 ? (
              <div className="text-center py-12 card-tactical">
                <Flame className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No active streaks</h3>
                <p className="text-gray-400">Complete habits daily to build your streak!</p>
              </div>
            ) : (
              streaksLeaderboard.map((entry) => (
                <LeaderboardRow key={entry.user_id} entry={entry} type="streaks" />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="weekly" className="mt-6">
          <div className="space-y-3">
            {weeklyLeaderboard.length === 0 ? (
              <div className="text-center py-12 card-tactical">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No activity this week</h3>
                <p className="text-gray-400">Complete habits to appear on the weekly board!</p>
              </div>
            ) : (
              weeklyLeaderboard.map((entry) => (
                <LeaderboardRow key={entry.user_id} entry={entry} type="weekly" />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="challenges" className="mt-6">
          <div className="space-y-4">
            {challenges.length === 0 ? (
              <div className="text-center py-12 card-tactical">
                <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No active challenges</h3>
                <p className="text-gray-400">Check back soon for new challenges!</p>
              </div>
            ) : (
              challenges.map((challenge) => {
                const myProgress = myChallenges.find(c => c.id === challenge.id);
                const joined = isJoined(challenge.id);
                
                return (
                  <div key={challenge.id} className="card-tactical">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Award className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg text-white font-semibold">{challenge.title}</h3>
                          <p className="text-gray-400 text-sm mt-1">{challenge.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-yellow-500 flex items-center gap-1">
                              <Star className="w-4 h-4" />
                              {challenge.reward_points} pts
                            </span>
                            <span className="text-gray-400">
                              Target: {challenge.target}
                            </span>
                          </div>
                          
                          {joined && myProgress && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-gray-400">Your Progress</span>
                                <span className="text-white">{myProgress.my_progress || 0}/{challenge.target}</span>
                              </div>
                              <Progress 
                                value={((myProgress.my_progress || 0) / challenge.target) * 100} 
                                className="h-2"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {!joined ? (
                        <Button
                          onClick={() => joinChallenge(challenge.id)}
                          className="bg-primary hover:bg-red-700"
                        >
                          Join Challenge
                        </Button>
                      ) : (
                        <div className="px-4 py-2 bg-green-500/20 text-green-500 rounded-sm text-sm">
                          Joined
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
